import React, { useMemo, useState } from "react";
import { X, Save, ArrowUp, ArrowDown, Trash2, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { ImageUploadField } from "@/pages/admin/fields/ImageUploadField";
import { BylineEditor, normalizeByline } from "@/pages/admin/fields/BylineEditor";
import { Section, TextField, IconBtn } from "@/pages/admin/fields/primitives";
import { IMAGE_RECOMMENDATIONS } from "@/pages/admin/adminImageMeta";
import { PopularPreview } from "@/components/popular/PopularPreview";

// ================================================================
// Creator Setups — rich editor
// (Phase 8.24 — Platform picker: tiers moved from a flat array to a
// per-platform structure. Legacy `form.tiers` is preserved on save so any
// consumer still reading it works, but the source of truth going forward
// is `form.platforms.{windows|mac}.tiers`.)
// ================================================================
const newTier = () => ({
  id: `tier_${Math.random().toString(36).slice(2, 10)}`,
  name: "",
  price: 0,
  description: "",
  features: [],
  popular: false,
  current_model: "",
});
const newAddon = () => ({
  id: `addon_${Math.random().toString(36).slice(2, 10)}`,
  name: "",
  price: 0,
  description: "",
});

const emptyPlatform = () => ({ active: false, badge: "", subtext: "", tiers: [] });

const normalizePlatforms = (item) => {
  const src = item?.platforms || {};
  const build = (key) => {
    const p = src[key] || {};
    return {
      active: !!p.active,
      badge: p.badge || "",
      subtext: p.subtext || "",
      tiers: (p.tiers || []).map((t) => ({
        id: t.id || `tier_${Math.random().toString(36).slice(2, 10)}`,
        name: t.name || "",
        price: Number(t.price || 0),
        description: t.description || "",
        features: Array.isArray(t.features) ? [...t.features] : [],
        popular: !!t.popular,
        current_model: t.current_model || "",
      })),
    };
  };
  const windows = build("windows");
  const mac = build("mac");
  // Back-compat: brand-new setups (no `platforms` on the record yet) inherit
  // any legacy top-level `tiers` array into Windows so the admin can keep
  // working without a migration round-trip.
  if (!src.windows && !src.mac && Array.isArray(item?.tiers) && item.tiers.length > 0) {
    windows.tiers = item.tiers.map((t) => ({
      ...newTier(),
      ...t,
      current_model: t.current_model || "",
    }));
    windows.active = true;
  }
  return { windows, mac };
};

export const SetupEditorDrawer = ({ item, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({
    slug: "",
    name: "",
    persona: "",
    price: 0,
    image: "",
    icon_url: "",
    byline: { text: "", bold: true, italic: false, size: "small" },
    short_description: "",
    long_description: "",
    features: [],
    tiers: [],
    addons: [],
    compatibility_guide: "",
    display_order: 0,
    ...(item || {}),
    byline: normalizeByline(item?.byline),
    platforms: normalizePlatforms(item),
  }));
  const [activeTab, setActiveTab] = useState("windows");

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: (k === "price" || k === "display_order") ? Number(v || 0) : v }));
  };

  // ---- Features (list of strings) ----
  const updateFeature = (i, v) => setForm((f) => ({ ...f, features: f.features.map((x, idx) => (idx === i ? v : x)) }));
  const addFeature = () => setForm((f) => ({ ...f, features: [...f.features, ""] }));
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  const moveFeature = (i, dir) => setForm((f) => {
    const arr = [...f.features];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, features: arr };
  });

  // ---- Platform helpers ----
  const setPlatform = (key, patch) =>
    setForm((f) => ({
      ...f,
      platforms: {
        ...f.platforms,
        [key]: { ...f.platforms[key], ...patch },
      },
    }));
  const setPlatformTiers = (key, updater) =>
    setForm((f) => ({
      ...f,
      platforms: {
        ...f.platforms,
        [key]: { ...f.platforms[key], tiers: updater(f.platforms[key].tiers || []) },
      },
    }));

  // ---- Tiers (scoped to activeTab platform) ----
  const p = form.platforms[activeTab];
  const updateTier = (i, k, v) => setPlatformTiers(activeTab, (tiers) =>
    tiers.map((t, idx) => (idx === i ? { ...t, [k]: k === "price" ? Number(v || 0) : v } : t))
  );
  const updateTierFeature = (i, j, v) => setPlatformTiers(activeTab, (tiers) =>
    tiers.map((t, idx) => (idx === i ? { ...t, features: (t.features || []).map((x, k) => (k === j ? v : x)) } : t))
  );
  const addTier = () => setPlatformTiers(activeTab, (tiers) => [...tiers, newTier()]);
  const removeTier = (i) => setPlatformTiers(activeTab, (tiers) => tiers.filter((_, idx) => idx !== i));
  const moveTier = (i, dir) => setPlatformTiers(activeTab, (tiers) => {
    const arr = [...tiers];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const addTierFeature = (i) => setPlatformTiers(activeTab, (tiers) =>
    tiers.map((t, idx) => (idx === i ? { ...t, features: [...(t.features || []), ""] } : t))
  );
  const removeTierFeature = (i, j) => setPlatformTiers(activeTab, (tiers) =>
    tiers.map((t, idx) => (idx === i ? { ...t, features: t.features.filter((_, k) => k !== j) } : t))
  );

  // ---- Addons ----
  const updateAddon = (i, k, v) => setForm((f) => ({
    ...f, addons: f.addons.map((a, idx) => (idx === i ? { ...a, [k]: k === "price" ? Number(v || 0) : v } : a)),
  }));
  const addAddon = () => setForm((f) => ({ ...f, addons: [...(f.addons || []), newAddon()] }));
  const removeAddon = (i) => setForm((f) => ({ ...f, addons: f.addons.filter((_, idx) => idx !== i) }));
  const moveAddon = (i, dir) => setForm((f) => {
    const arr = [...f.addons];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, addons: arr };
  });

  const commit = () => {
    if (!form.slug?.trim()) {
      toast.error("Slug is required (used in the setup URL, e.g. 'graphic-design')");
      return;
    }
    // Persist a flat `tiers` copy of the Windows platform for legacy consumers
    // (e.g. any cached client still reading the old shape). Source of truth
    // remains `platforms.*.tiers`.
    const legacyTiers = form.platforms.windows.tiers || [];
    onSave({ ...form, tiers: legacyTiers });
  };

  const platformLabel = { windows: "Windows", mac: "Mac" };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-end" onClick={onClose}>
      <aside data-testid="setup-editor-drawer" className="bg-[#0F0F0F] w-full max-w-2xl h-full border-l border-[#262626] p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6 sticky top-0 -mt-6 -mx-6 px-6 py-4 bg-[#0F0F0F] border-b border-[#262626] z-10">
          <div>
            <div className="ur-eyebrow">Creator Setup</div>
            <h3 className="font-display text-xl font-bold mt-1">{item?.id ? `Edit · ${item.name || form.name || "Setup"}` : "Create Setup"}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="setup-save" onClick={commit} className="ur-btn-primary text-sm py-2.5 px-4">
              Save <Save className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Basics */}
        <Section
          title="Basics"
          destination="Renders on: Homepage → Creator Setups card AND /setups/[slug] product page hero"
          hint="Core metadata shown on the homepage card + product detail page."
        >          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Slug" value={form.slug} onChange={setField("slug")} placeholder="graphic-design" hint="Used in URL: /setups/{slug}" />
            <TextField label="Name" value={form.name} onChange={setField("name")} placeholder="Graphic Design Setup" />
            <TextField label="Persona" value={form.persona} onChange={setField("persona")} placeholder="graphic_design / architecture / business" />
            <TextField label="Starting Price (AWG)" type="number" value={form.price} onChange={setField("price")} />
          </div>
          <BylineEditor value={form.byline} onChange={(v) => setForm((f) => ({ ...f, byline: v }))} />
          <TextField label="Short Description" value={form.short_description} onChange={setField("short_description")} textarea rows={2} />
          <TextField label="Long Description" value={form.long_description} onChange={setField("long_description")} textarea rows={3} hint="Shown on the /setups/:slug detail page hero." />
        </Section>

        {/* Images */}
        <Section
          title="Images"
          destination="Renders on: Homepage → Creator Setups card image + icon"
          hint="Laptop product photo + small line icon shown on the homepage card."
        >          <ImageUploadField
            label="Laptop Image"
            value={form.image}
            onChange={(v) => setForm({ ...form, image: v })}
            testId="upload-image"
            recommended={IMAGE_RECOMMENDATIONS["laptops.image"]}
          />
          <ImageUploadField
            label="Icon"
            value={form.icon_url}
            onChange={(v) => setForm({ ...form, icon_url: v })}
            testId="upload-icon_url"
            recommended={IMAGE_RECOMMENDATIONS["laptops.icon_url"]}
          />
        </Section>

        {/* Features checklist */}
        <Section
          title="Checklist Items"
          destination="Renders on: Homepage → Creator Setups card (orange-check bullet list)"
          hint="Reorder with the arrows. Independent of tiers/add-ons — this is just the on-card bullet list."
        >          <div className="space-y-2">
            {form.features.map((f, i) => (
              <div key={i} data-testid={`feature-row-${i}`} className="flex items-center gap-2">
                <input
                  value={f}
                  onChange={(e) => updateFeature(i, e.target.value)}
                  className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
                />
                <IconBtn onClick={() => moveFeature(i, -1)} disabled={i === 0}><ArrowUp className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => moveFeature(i, 1)} disabled={i === form.features.length - 1}><ArrowDown className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => removeFeature(i)} danger><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            ))}
            <button data-testid="add-feature" onClick={addFeature} className="ur-btn-secondary text-xs py-2 px-3">
              <Plus className="w-3.5 h-3.5" /> Add Checklist Item
            </button>
          </div>
        </Section>

        {/* Platforms — tabbed Windows/Mac editor. Tier specs, pricing and
            model are fully independent per platform (per spec). Each
            platform also carries an `active` toggle: inactive platforms are
            hidden from customers entirely, not rendered as broken options. */}
        <Section
          title="Platforms — Windows / Mac"
          destination="Renders on: /setups/[slug] → Step 1 (platform picker) + Step 2 (tier picker for the selected platform)"
          hint="Each workstation × platform combination is fully independent. Toggle a platform off if it isn't commercially supported yet — the card won't render for customers. The Windows tier data was migrated from the legacy flat tier list; Mac tiers were seeded as a copy on your active platforms, edit them freely."
        >
          <div className="flex items-center gap-2 mb-4">
            {["windows", "mac"].map((k) => (
              <button
                key={k}
                data-testid={`platform-tab-${k}`}
                onClick={() => setActiveTab(k)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                  activeTab === k
                    ? "bg-[#FF6600] text-black"
                    : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#FF6600]/40"
                }`}
              >
                {platformLabel[k]}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Active</label>
              <input
                data-testid={`platform-${activeTab}-active`}
                type="checkbox"
                checked={!!p.active}
                onChange={(e) => setPlatform(activeTab, { active: e.target.checked })}
                className="w-4 h-4 accent-[#FF6600]"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <TextField
              label={`${platformLabel[activeTab]} Badge (optional)`}
              value={p.badge}
              onChange={(e) => setPlatform(activeTab, { badge: e.target.value })}
              placeholder='e.g. "Recommended" — leave blank for no badge'
              hint='Renders as an orange "Star" pill on the platform card. Only the exact text "Recommended" is remapped through the CMS "Recommended" label field so you can translate it site-wide; any other badge text renders as-is.'
            />
            <TextField
              label={`${platformLabel[activeTab]} Subtext (optional)`}
              value={p.subtext}
              onChange={(e) => setPlatform(activeTab, { subtext: e.target.value })}
              placeholder="Short caveat or recommendation shown under the card label"
              hint='For Architecture Windows we default to a "Best compatibility for CAD/BIM" line; the Mac card carries a "software availability may vary" caveat. Adjust per workstation to suit.'
              textarea
              rows={2}
            />
          </div>

          <div className="mt-6 space-y-4">
            <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">
              {platformLabel[activeTab]} Tiers · {p.tiers.length}
            </div>
            {(p.tiers || []).map((t, i) => (
              <div
                key={t.id || i}
                data-testid={`platform-${activeTab}-tier-row-${i}`}
                className="rounded-2xl border border-[#262626] bg-[#141414] p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-[#FF6600] font-bold">
                    Tier #{i + 1}
                  </div>
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => moveTier(i, -1)} disabled={i === 0}><ArrowUp className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => moveTier(i, 1)} disabled={i === p.tiers.length - 1}><ArrowDown className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => removeTier(i)} danger><Trash2 className="w-4 h-4" /></IconBtn>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <TextField label="Name" value={t.name} onChange={(e) => updateTier(i, "name", e.target.value)} />
                  <TextField label="Price (AWG)" type="number" value={t.price} onChange={(e) => updateTier(i, "price", e.target.value)} />
                  <label className="flex items-end gap-2 text-xs uppercase tracking-widest text-[#A3A3A3]">
                    <input type="checkbox" checked={!!t.popular} onChange={(e) => updateTier(i, "popular", e.target.checked)} className="w-4 h-4 accent-[#FF6600]" />
                    <span className="pb-3">Popular badge</span>
                  </label>
                </div>
                {t.popular && (
                  <PopularPreview kind="card" name={t.name} testId={`platform-${activeTab}-tier-${i}-popular-preview`} />
                )}
                <TextField label="Description" value={t.description} onChange={(e) => updateTier(i, "description", e.target.value)} textarea rows={2} />
                <TextField
                  label="Current Model (shown on tier card)"
                  value={t.current_model}
                  onChange={(e) => updateTier(i, "current_model", e.target.value)}
                  placeholder='e.g. "HP Victus 16" — leave blank to hide the row'
                  hint='Renders on the tier card as "Current model / [value]" + a "Model may vary based on availability" caption. When blank, the entire row is hidden (no placeholder text).'
                />
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#A3A3A3] mb-2">Tier Features</div>
                  <div className="space-y-1.5">
                    {(t.features || []).map((tf, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <input
                          value={tf}
                          onChange={(e) => updateTierFeature(i, j, e.target.value)}
                          className="flex-1 bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                        />
                        <IconBtn onClick={() => removeTierFeature(i, j)} danger><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                      </div>
                    ))}
                    <button onClick={() => addTierFeature(i)} className="text-xs text-[#FF6600] font-semibold hover:underline">+ Add tier feature</button>
                  </div>
                </div>
              </div>
            ))}
            <button
              data-testid={`platform-${activeTab}-add-tier`}
              onClick={addTier}
              className="ur-btn-secondary text-xs py-2 px-3"
            >
              <Plus className="w-3.5 h-3.5" /> Add {platformLabel[activeTab]} Tier
            </button>
          </div>
        </Section>

        {/* Compatibility Guide — per-workstation copy shown in the Step 1
            modal. Kept as free-form text (line breaks preserved) so Calvin
            can drop in a short guide, a bulleted list, or a longer FAQ
            without needing rich-text infrastructure. */}
        <Section
          title="Compatibility Guide"
          destination='Renders on: /setups/[slug] → modal that opens from the Step 1 "View compatibility guide" link'
          hint="Plain-language notes explaining which software works on which platform for THIS workstation. Line breaks are preserved. Leave blank to fall back to the site-wide empty-state (edited in Site Settings)."
        >
          <TextField
            label="Compatibility Notes"
            value={form.compatibility_guide}
            onChange={(e) => setForm((f) => ({ ...f, compatibility_guide: e.target.value }))}
            textarea
            rows={6}
            placeholder="e.g.\n• AutoCAD, Revit, Enscape — Windows only\n• SketchUp, D5 Render — Windows recommended, Mac available for design review\n• General office + Adobe Suite — both platforms fully supported"
          />
        </Section>

        {/* Addons */}
        <Section
          title="Add-ons"
          destination="Renders on: /setups/[slug] product page → Step 3 add-ons list (customers can pick multiple)"
          hint="Each price stacks on top of the tier price. Add-ons are shared across platforms — they aren't platform-specific."
        >          <div className="space-y-3">
            {(form.addons || []).map((a, i) => (
              <div key={a.id || i} data-testid={`addon-row-${i}`} className="rounded-2xl border border-[#262626] bg-[#141414] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-[#FF6600] font-bold">Add-on #{i + 1}</div>
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => moveAddon(i, -1)} disabled={i === 0}><ArrowUp className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => moveAddon(i, 1)} disabled={i === form.addons.length - 1}><ArrowDown className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => removeAddon(i)} danger><Trash2 className="w-4 h-4" /></IconBtn>
                  </div>
                </div>
                <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
                  <TextField label="Name" value={a.name} onChange={(e) => updateAddon(i, "name", e.target.value)} />
                  <TextField label="Price (AWG)" type="number" value={a.price} onChange={(e) => updateAddon(i, "price", e.target.value)} />
                </div>
                <TextField label="Description" value={a.description} onChange={(e) => updateAddon(i, "description", e.target.value)} />
              </div>
            ))}
            <button data-testid="add-addon" onClick={addAddon} className="ur-btn-secondary text-xs py-2 px-3">
              <Plus className="w-3.5 h-3.5" /> Add Add-on
            </button>
          </div>
        </Section>

        <div className="mt-8 sticky bottom-0 -mx-6 px-6 py-4 bg-[#0F0F0F] border-t border-[#262626]">
          <button data-testid="setup-save-bottom" onClick={commit} className="ur-btn-primary w-full">
            Save Setup <Save className="w-4 h-4" />
          </button>
        </div>
      </aside>
    </div>
  );
};
